import React from "react";
import { motion } from "framer-motion";
import { HOME } from "@/constants/testIds";
import ReserveButton from "@/components/ReserveButton";

const APPAREL_ITEMS = [
  { n: "01", name: "Walk of Faith", sub: "Oversized Shirt", cat: "TOP · OVERSIZED", line: "MBS", img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=800&q=80&auto=format&fit=crop" },
  { n: "02", name: "Half My Pain", sub: "Cropped Tank Top", cat: "TANK · CROPPED", line: "MBS", img: "https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=800&q=80&auto=format&fit=crop" },
  { n: "03", name: "Rules of MBS", sub: "Long Sleeve", cat: "LONG SLEEVE", line: "MBS", img: "https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=800&q=80&auto=format&fit=crop" },
  { n: "04", name: "Rules of MBS", sub: "Short Sleeve", cat: "TEE · SHORT SLEEVE", line: "MBS", img: "https://images.unsplash.com/photo-1503341504253-dff4815485f1?w=800&q=80&auto=format&fit=crop" },
  { n: "05", name: "WMD", sub: "Sleeved Shirt", cat: "SIGNATURE · SLEEVED", line: "MBS", img: "https://images.unsplash.com/photo-1622445275463-afa2ab738c34?w=800&q=80&auto=format&fit=crop" },
  { n: "06", name: "Overload Jackets", sub: "Outerwear Range", cat: "OUTERWEAR", line: "MBS", img: "https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=800&q=80&auto=format&fit=crop" },
  { n: "07", name: "Animal", sub: "Compression Gear", cat: "PERFORMANCE", line: "MBS", img: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&q=80&auto=format&fit=crop" },
  { n: "08", name: "MBS Socks", sub: "Numbered Range", cat: "FOOTWEAR · SOCKS", line: "MBS", img: "https://images.unsplash.com/photo-1586350977771-b3714cca5ea2?w=800&q=80&auto=format&fit=crop" },
  { n: "09", name: "Journey Level Beanie", sub: "Ribbed Fold Beanie", cat: "HEADWEAR · BEANIE", line: "JOURNEY LEVEL", img: "https://images.unsplash.com/photo-1576871337622-98d48d1cf531?w=800&q=80&auto=format&fit=crop" },
  { n: "10", name: "Journey Level Sweat Shorts", sub: "Heavyweight Fleece", cat: "BOTTOMS · SHORTS", line: "JOURNEY LEVEL", img: "https://images.unsplash.com/photo-1591195853828-11db59a44f6b?w=800&q=80&auto=format&fit=crop" },
  { n: "11", name: "Journey Level Joggers", sub: "Tapered Fleece Joggers", cat: "BOTTOMS · JOGGERS", line: "JOURNEY LEVEL", img: "https://images.unsplash.com/photo-1552902865-b72c031ac5ea?w=800&q=80&auto=format&fit=crop" },
];

const TileFrame = ({ n, cat, line, img, hover }) => (
  <div className="relative aspect-[4/5] overflow-hidden bg-black">
    {/* Product photograph (grayscale placeholder) */}
    {img && (
      <img
        src={img}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover grayscale contrast-[1.05] opacity-70 transition-transform duration-1000 group-hover:scale-105"
      />
    )}
    {/* Frame border */}
    <div className="absolute inset-0 border border-white/12 z-20 pointer-events-none" />
    {/* Clipped corner */}
    <div
      className="absolute top-0 right-0 w-12 h-12 z-20 bg-black"
      style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }}
    />
    <div className="absolute top-2 right-2 z-30 font-mono text-[8px] tracking-[0.32em] text-silver-200">
      N° {n}
    </div>
    {line && (
      <div className="absolute top-2 left-2 z-30 font-mono text-[8px] tracking-[0.35em] text-silver-100/80">
        {line}
      </div>
    )}

    {/* Engraved diagonal + darkening overlays */}
    <div className="absolute inset-0">
      {/* Dark overlay so COMING SOON reads over the photo */}
      <div className="absolute inset-0 bg-black/70" />
      <div
        className="absolute inset-0 opacity-[0.16] transition-opacity duration-700 group-hover:opacity-[0.28]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, transparent 0, transparent 18px, rgba(229,228,226,0.35) 18px, rgba(229,228,226,0.35) 19px)",
        }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(229,228,226,0.14)_0%,transparent_60%)] transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 grain-overlay opacity-[0.12]" />
    </div>

    {/* Category footer */}
    <div className="absolute bottom-3 left-3 right-3 z-30 flex items-center justify-between font-mono text-[8px] tracking-[0.3em] text-silver-300/80">
      <span>{cat}</span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-1 h-1 rounded-full bg-silver-100 animate-pulse" />
        SOON
      </span>
    </div>

    {/* Big COMING SOON typeset */}
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4 text-center transition-transform duration-700 group-hover:-translate-y-2">
      <div
        className="font-serif leading-[0.85] tracking-[-0.02em] text-[16vw] md:text-[3.5vw]"
        style={{
          WebkitTextStroke: "1px rgba(229,228,226,0.42)",
          color: "transparent",
        }}
      >
        COMING
      </div>
      <div className="font-serif italic text-silver-100 leading-[0.85] tracking-[-0.02em] text-[16vw] md:text-[3.5vw] mt-1">
        soon.
      </div>
    </div>

    {/* Corner ticks */}
    <div className="absolute top-2 left-2 z-30 w-2.5 h-2.5 border-l border-t border-white/40" />
    <div className="absolute bottom-2 left-2 z-30 w-2.5 h-2.5 border-l border-b border-white/40" />

    {/* Hover sweep */}
    <div className="absolute inset-x-0 top-0 h-px bg-silver-100 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 z-30" />

    {hover && (
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-white/[0.06] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    )}
  </div>
);

export const Apparel = () => {
  return (
    <section
      id="apparel"
      data-testid={HOME.apparel}
      className="relative bg-black text-white py-32 md:py-40"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="flex items-end justify-between mb-16 md:mb-24 border-b border-white/10 pb-8"
        >
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                §02 — APPAREL
              </span>
              <span className="inline-flex items-center gap-2 px-2.5 py-1 border border-silver-100/40 rounded-full font-mono text-[9px] tracking-[0.32em] uppercase text-silver-100">
                <span className="w-1.5 h-1.5 rounded-full bg-silver-100 animate-pulse" />
                Coming Soon
              </span>
            </div>
            <h2 className="font-serif text-[10vw] md:text-[5vw] leading-[0.95] tracking-[-0.02em] text-white">
              Uniforms for
              <br />
              <span className="italic text-silver-100">the quiet order.</span>
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <ReserveButton items={[]} label="Reserve Drop" />
            <a
              href="#enlist"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/70 hover:text-white transition-colors group"
            >
              Join the drop list
              <span className="w-10 h-px bg-silver-100 group-hover:w-16 transition-all" />
            </a>
          </div>
        </motion.div>

        {/* Grid of apparel items */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {APPAREL_ITEMS.map((item, i) => (
            <motion.a
              key={item.n}
              href="#enlist"
              data-testid={`apparel-item-${item.n}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                duration: 0.7,
                delay: (i % 4) * 0.06,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group block"
            >
              <TileFrame n={item.n} cat={item.cat} line={item.line} img={item.img} hover />
              <div className="mt-4 md:mt-5 px-1">
                <h3 className="font-serif text-white text-lg md:text-xl leading-tight tracking-[-0.01em]">
                  {item.name}
                </h3>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <div className="font-serif italic text-silver-200 text-[13px] md:text-sm">
                    {item.sub}
                  </div>
                  <span className="font-mono text-[9px] tracking-[0.28em] text-white/40 whitespace-nowrap">
                    NOTIFY →
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Footer strip */}
        <div className="mt-16 md:mt-20 border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-white/50 font-mono text-[10px] tracking-[0.32em] uppercase">
          <div>11 pieces · numbered · limited runs · MBS + Journey Level lines</div>
          <a
            href="#enlist"
            className="text-silver-100 hover:text-white transition-colors flex items-center gap-3"
          >
            Get the drop dispatch
            <span className="w-8 h-px bg-silver-100" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Apparel;
